import React, { useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { BasisTheoryProvider, useBasisTheory } from '@basis-theory/react-elements';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import * as Sentry from '@sentry/react';
import { fromJS } from 'immutable';
import flowRight from 'lodash-es/flowRight';
import { paymentMethod as PaymentMethods, acceptableCancelCodesForRewrite } from '@ourbranch/lookups';
import { isFuture, parseISO } from 'date-fns';

import { hydrateFarmPartyForForm } from 'common/helpers/farm-interested-party-helpers';
import config from 'aws-exports';
import { useBasisTheoryElementRefsProvider } from 'common/hooks/basis-theory-elements.context';
import { appConfig } from 'brand-config';
import { withStore } from 'core/store';
import useSession from 'core/hooks/use-session';
import { withToast } from 'core/components/toast/context';
import { track } from 'core/helpers/analytics';
import {
  getPolicyStatus,
  PolicyStatus,
  PolicyChangeAction,
  isImportedAndNotEndorseable
} from 'core/helpers/policy-status';
import { showExpirationInsteadOfEndDate, endDateToExpirationDisplay } from 'core/helpers/expiration-date';
import { ValidationErrorsModal, useValidationErrorsModal } from 'core/components/validation-errors-modal';
import { getStartMinMaxDate } from '../dates';
import Footer from '../footer';
import CancelledPolicyFooter from '../footer/cancelled-policy-footer';
import ImportedPolicyFooter from '../footer/imported-policy-footer';
import CancellationForm from '../footer/cancellation-form';
import { defaultRefundCreditCardFee } from '../footer/cancellation-form/refund-credit-card-fee';
import NonRenewForm from '../footer/non-renew-form';
import ReinstateForm from '../footer/reinstate-form';
import { AddHoldCard } from '../hold-card';
import RewriteModal from '../rewrite-modal';
import { validationSchema } from './policy-settings.validation';
import { getScreenAction, POLICY_CHANGE_ACTION_FOOTER_LABEL } from './screen-action';
import { endDateValue } from './end-date-value';
import PolicySettings from './policy-settings';
import NoReinstatementModal from './no-reinstatement-modal';
import useStyles from './policy-settings.styles';

const stripePromise = loadStripe(config.stripeKey);

const parseValues = (values, screenAction) => {
  const isReinstatementOrRescind =
    screenAction === PolicyChangeAction.Reinstate || screenAction === PolicyChangeAction.Rescind;
  return {
    ...values,
    cancelReason: values.cancelReason,
    nonRenewReason: values.autoRenew ? 'NA' : values.nonRenewReason,
    nonRenewed: !values.autoRenew,
    endDate: endDateValue(values),
    // for reinstatement, don't send in fees because the backend will determine what fees to generate on a reinstatement
    fees: isReinstatementOrRescind ? undefined : values.fees,
    fullTermCancel: isReinstatementOrRescind ? false : values.fullTermCancel
  };
};

const screenActionFor = (values) =>
  getScreenAction({
    cancel: values.cancel,
    fullTermCancel: values.fullTermCancel,
    transactionDate: values.transactionDate,
    storedEndDate: endDateValue(values),
    fullTermPolicyEndDate: values.fullTermPolicyEndDate,
    state: values.state
  });

const doesPolicyHaveCancelledPriorTerm = (policy, policies) => {
  if (policy.term === 1 || policies.length === 1) {
    // first term or only policy should not have cancelled prior
    return false;
  }

  // use 15 digits in case of home2 / auto3 etc.
  const priorTerm = policies.find(
    (p) => p.id.substring(0, 15) === policy.id.substring(0, 15) && Number(p.id.split('-')[2]) === policy.term - 1
  );

  if (priorTerm && isFuture(parseISO(policy.effectiveDate)) && priorTerm.endDate !== priorTerm.fullTermPolicyEndDate) {
    // check if the new policy would have started already, if so we don't need to reopen the prior term
    return priorTerm.id;
  }

  return false;
};

const isPolicyEligibleForRewrite = (policy, canRewritePolicy) => {
  if (!canRewritePolicy) {
    return false;
  }
  const flatCancel = policy?.effectiveDate === policy?.endDate;
  const firstTerm = policy?.term === 1;
  return firstTerm && flatCancel && acceptableCancelCodesForRewrite.includes(policy?.cancelReason);
};

const PolicySettingsContainer = ({
  loadingPreview,
  onPreviewPolicy,
  accountId,
  cancelledOrInCancellation,
  allowRescind,
  allowReinstatement,
  disableLeavePageModal,
  store: {
    account: {
      policies: {
        policy: { segment, setChanged, policy, addHoldCard },
        list: policies
      }
    }
  },
  toast
}) => {
  const [state, setState] = useState(
    fromJS({
      isNonRenew: false,
      effectiveDateTouched: false,
      modal: ''
    })
  );

  const classes = useStyles();
  const { showValidationErrorsModal, setShowValidationErrorsModal } = useValidationErrorsModal();
  const { canBackDate, isAgency, canEdit, viewOnly, canRewritePolicy, canReinstateCNPY } = useSession();

  const policyEligibleForRewrite = isPolicyEligibleForRewrite(policy, canRewritePolicy);
  const policyStatus = getPolicyStatus(policy);
  const { bt } = useBasisTheory(config.basisTheoryApiKey, {
    elements: true,
    environment: config.basisTheoryElementsEnv
  });
  const { Provider: BasisTheoryElementsProvider, refs: elementRefs } = useBasisTheoryElementRefsProvider();

  const [showNoReinstateForCNPYModal, setShowNoReinstateForCNPYModal] = useState(false);
  const [showRewriteModal, setShowRewriteModal] = useState(false);
  const [showNoReinstateForPriorCancelledModal, setShowNoReinstateForPriorCancelledModal] = useState(false);

  const { minDate, maxDate } = getStartMinMaxDate({
    policy,
    editCurrentSegment: true,
    currentSegment: policy.segments[0],
    canBackDate,
    editingPolicySettings: true,
    policyType: policy.policyType,
    isAgency
  });

  const priorCancelledTerm = doesPolicyHaveCancelledPriorTerm(policy, policies);

  // Permission decides which control the cancelled-policy footer offers; the click declares the action (onSubmit).
  const cancelledPolicyFooterAction =
    (allowReinstatement && PolicyChangeAction.Reinstate) || (allowRescind && PolicyChangeAction.Rescind) || undefined;
  const isReinstatementOrRescind = Boolean(cancelledPolicyFooterAction);
  const cancelledPolicyFooterLabel = POLICY_CHANGE_ACTION_FOOTER_LABEL[cancelledPolicyFooterAction] || false;
  const declaredActionRef = useRef();

  const handleAddHoldCard = useCallback(() => {
    setState(state.setIn(['modal'], 'addHoldCard'));
  }, [setState, state]);

  const closeModal = useCallback(() => {
    setState(state.setIn(['modal'], ''));
  }, [setState, state]);

  const handleChangePolicy = useCallback(
    (values) => {
      const screenAction = declaredActionRef.current ?? screenActionFor(values);
      declaredActionRef.current = undefined;
      onPreviewPolicy(parseValues(values, screenAction), screenAction);
    },
    [onPreviewPolicy]
  );

  const onHoldCardAddition = useCallback(
    (args) => {
      const { reason, policyId } = args;
      addHoldCard(reason, policyId)
        .then(() => {
          track('Staff Add Hold Card', { reason, policyId });
          toast.notify({
            type: 'success',
            message: 'The hold card was added successfully'
          });
        })
        .catch((res) => {
          Sentry.captureException(
            JSON.stringify({
              res,
              body: {
                reason,
                policyId
              }
            })
          );
        });
    },
    [addHoldCard, toast]
  );

  return (
    <Formik
      initialValues={{
        ...policy,
        // When the brand displays the expiration date instead of endDate, seed the form with the
        // expiration (endDate + 1) for an in-force policy so the field shows/edits expiration; the
        // stored value is restored on save via endDateValue. Flat cancellations are left as-is. (#32070)
        endDate:
          showExpirationInsteadOfEndDate() && policy.endDate && policy.endDate !== policy.effectiveDate
            ? endDateToExpirationDisplay(policy.endDate)
            : policy.endDate,
        // farmInterestedParties is a policy-level field (like the other settings-tab fields), not a
        // segment field — so it must be read from `policy`, not the segment-merged `policyDetails`.
        farmInterestedParties: (policy.farmInterestedParties || []).map(hydrateFarmPartyForForm),
        // #34376: supplemental-documents multi-select holds the selected catalog ids (policy-level,
        // like farmInterestedParties). Default to [] so the chipArray is controlled.
        supplementalDocuments: policy.supplementalDocuments || [],
        renewalCreditPull: !!policy.renewalCreditPull,
        blockCreditCheckAtRenewal: policy?.blockCreditCheckAtRenewal || false,
        cancellationEndDate: policy.endDate,
        isNonRenew: false,
        cancelReason: policy.cancelReason === 'NA' ? undefined : policy.cancelReason, // clear out the NA value because policies are bound with this
        nonRenewReason: policy.nonRenewReason === 'NA' ? undefined : policy.nonRenewReason, // clear out the NA value because policies are bound with this
        skipImmediateBillOrRefund: policy.skipImmediateBillOrRefund,
        salesRep: policy.salesRep || policy?.offer?.quote?.rep,
        applyFee: policyStatus === PolicyStatus.Cancelled,
        // #36221: "Refund credit card fee" on the cancellation form — brand default (keep by default)
        refundCreditCardFee: defaultRefundCreditCardFee(appConfig)
      }}
      validationSchema={() => {
        if (cancelledOrInCancellation) {
          // dont validate anything on reinstatements/recinding cancellations - cant return undefined for some reason
          return Yup.object();
        }
        try {
          return Yup.lazy((values) =>
            validationSchema({
              policyType: policy.policyType,
              values,
              minDate,
              maxDate,
              currentEffectiveDate: policy.effectiveDate,
              wasEffectiveDateUpdated:
                policy.effectiveDate !== values.effectiveDate && state.get('effectiveDateTouched'),
              policyStatus,
              term: policy.term,
              agentCanBackDate: canBackDate
            })
          );
        } catch (e) {
          if (e?.name?.includes('TypeError')) {
            console.error(e);
            Sentry.captureException(e, {
              extra: {
                inPolicySettings: true,
                validationFailedToRun: true,
                policyId: policy.id,
                policyType: policy.policyType,
                policyStatus
              }
            });
          }
          return Yup.object();
        }
      }}
      onSubmit={handleChangePolicy}
    >
      {({ handleSubmit, touched, values, validateForm, setErrors, setTouched }) => {
        const { paymentMethod } = values;
        const changed = canEdit && Object.keys(touched).length > 0;
        const cancelAction = screenActionFor(values);
        const previewPolicyCancellation = cancelAction === PolicyChangeAction.Cancel;

        // will submit form and show validation errors if there are any
        const handleKeepCurrentInfo = async () => {
          const errors = await validateForm();
          if (Object.keys(errors).length) {
            setErrors(errors);
            setTouched(errors);
            setShowValidationErrorsModal(true);
          } else {
            handleSubmit();
          }
        };

        // handles modals to show before submitting the form and checking for validation errors
        const onSubmit = async (declaredAction) => {
          setState(state.setIn(['effectiveDateTouched'], !!touched.effectiveDate));

          // if the policy is CANCELLED and it was cancelled due to non-payment then we need to show the no reinstatement modal
          // exceptions: if user has permission group or the policy is Escrow payment method
          if (
            policyStatus === PolicyStatus.Cancelled &&
            policy?.cancelReason &&
            policy.cancelReason === 'CNPY' &&
            !canReinstateCNPY &&
            paymentMethod !== PaymentMethods.Escrow
          ) {
            setShowNoReinstateForCNPYModal(true);
            return;
          }
          if (cancelledOrInCancellation && isReinstatementOrRescind && priorCancelledTerm) {
            setShowNoReinstateForPriorCancelledModal(true);
            return;
          }
          declaredActionRef.current = declaredAction;
          handleKeepCurrentInfo();
        };

        setChanged(changed);

        return (
          <BasisTheoryProvider bt={bt}>
            <BasisTheoryElementsProvider value={elementRefs}>
              {(showNoReinstateForCNPYModal || showNoReinstateForPriorCancelledModal) && (
                <NoReinstatementModal
                  open={showNoReinstateForCNPYModal || showNoReinstateForPriorCancelledModal}
                  setShowNoReinstateForCNPYModal={setShowNoReinstateForCNPYModal}
                  showNoReinstateForCNPYModal={showNoReinstateForCNPYModal}
                  setShowNoReinstateForPriorCancelledModal={setShowNoReinstateForPriorCancelledModal}
                  showNoReinstateForPriorCancelledModal={showNoReinstateForPriorCancelledModal}
                  priorCancelledTerm={priorCancelledTerm}
                />
              )}

              <Elements stripe={stripePromise}>
                <div className={changed || cancelledOrInCancellation ? classes.changed : null}>
                  <PolicySettings
                    policy={values}
                    changed={changed}
                    segment={segment}
                    handleAddHoldCard={handleAddHoldCard}
                    accountId={accountId}
                    minDate={minDate}
                    maxDate={maxDate}
                    cancelledOrInCancellation={cancelledOrInCancellation}
                  />

                  {!viewOnly &&
                    (isImportedAndNotEndorseable(policy) ? (
                      <ImportedPolicyFooter />
                    ) : cancelledOrInCancellation ? (
                      isReinstatementOrRescind || policyEligibleForRewrite ? (
                        <Footer
                          title={cancelledPolicyFooterLabel}
                          buttonLabel={cancelledPolicyFooterLabel}
                          form={priorCancelledTerm ? null : <ReinstateForm />}
                          loading={loadingPreview}
                          onClick={() => onSubmit(cancelledPolicyFooterAction)}
                          setShowRewriteModal={setShowRewriteModal}
                          isPolicyEligibleForRewrite={policyEligibleForRewrite}
                          policy={policy}
                          disableLeavePageModal={disableLeavePageModal}
                        />
                      ) : (
                        <CancelledPolicyFooter
                          policyStatus={policyStatus}
                          isAgency={isAgency}
                          endDate={policy.endDate}
                          effectiveDate={policy.effectiveDate}
                          policy={policy}
                        />
                      )
                    ) : (
                      changed &&
                      (values.isNonRenew ? (
                        <Footer
                          title="Cancel Renewal"
                          form={<NonRenewForm />}
                          loading={loadingPreview}
                          onClick={() => onSubmit()}
                          policy={policy}
                          disableLeavePageModal={disableLeavePageModal}
                        />
                      ) : (
                        <Footer
                          title={POLICY_CHANGE_ACTION_FOOTER_LABEL[cancelAction] || 'Save Changes'}
                          buttonLabel={previewPolicyCancellation ? 'Preview Cancellation' : undefined}
                          form={previewPolicyCancellation && <CancellationForm />}
                          loading={loadingPreview}
                          onClick={() => onSubmit()}
                          policy={policy}
                          disableLeavePageModal={disableLeavePageModal}
                        />
                      ))
                    ))}
                  {state.getIn(['modal']) === 'addHoldCard' && (
                    <AddHoldCard
                      policy={policy}
                      onClose={closeModal}
                      onHoldCardAddition={onHoldCardAddition}
                      open={state.getIn(['modal']) === 'addHoldCard'}
                    />
                  )}
                  <ValidationErrorsModal
                    open={showValidationErrorsModal}
                    onClose={() => setShowValidationErrorsModal(false)}
                  />
                  {showRewriteModal && <RewriteModal setShowRewriteModal={setShowRewriteModal} />}
                </div>
              </Elements>
            </BasisTheoryElementsProvider>
          </BasisTheoryProvider>
        );
      }}
    </Formik>
  );
};
PolicySettingsContainer.propTypes = {
  policy: PropTypes.shape({
    renewalCreditPull: PropTypes.bool,
    policyType: PropTypes.string.isRequired,
    effectiveDate: PropTypes.string.isRequired,
    endDate: PropTypes.string.isRequired,
    billingDayOfMonth: PropTypes.number.isRequired,
    autoRenew: PropTypes.bool,
    state: PropTypes.string.isRequired,
    term: PropTypes.number.isRequired,
    version: PropTypes.number.isRequired
  }).isRequired,
  loadingPreview: PropTypes.bool.isRequired,
  onPreviewPolicy: PropTypes.func.isRequired,
  toast: PropTypes.object.isRequired,
  store: PropTypes.object.isRequired,
  accountId: PropTypes.string.isRequired,
  cancelledOrInCancellation: PropTypes.bool.isRequired,
  allowRescind: PropTypes.bool.isRequired,
  allowReinstatement: PropTypes.bool.isRequired
};

export default flowRight(withToast, withStore)(PolicySettingsContainer);
